import { useState } from 'react';
import {
  Stack,
  Box,
  Typography,
  Button,
  Menu,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import {
  useDownloadAgentDebPackageMutation,
  useDownloadAgentRpmPackageMutation,
  useDownloadAgentExePackageMutation,
} from '../../services/ADapi';

import { parseDownloadErrorMsg } from '../../utils/parseDownloadErrorMsg';
import { downloadFile } from '../../utils/downloadFile';

import Icon from '../Icon';

const Card = styled(Stack)({
  padding: 24,
  border: '1px solid #CED2DA',
  borderRadius: '8px',
  background: '#fff',
});

const ReleaseCard = ({
  osName,
  agentVersion,
  releaseDate,
  changelog,
  downloadMethods,
  isDownloadTriggered,
  setDownloadTriggerState,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const open = Boolean(anchorEl);

  const [downloadDebPackage, { isLoading: isDebPackageLoading }] =
    useDownloadAgentDebPackageMutation();
  const [downloadRpmPackage, { isLoading: isRpmPackageLoading }] =
    useDownloadAgentRpmPackageMutation();
  const [downloadExePackage, { isLoading: isExePackageLoading }] =
    useDownloadAgentExePackageMutation();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDebPackageDownload = async () => {
    setErrorMsg('');
    handleClose();
    setDownloadTriggerState(true);

    try {
      const data = await downloadDebPackage().unwrap();
      downloadFile(data, 'Agent-Setup.deb');
    } catch (error) {
      const msg = parseDownloadErrorMsg(error);
      setErrorMsg(msg);
    }

    setDownloadTriggerState(false);
  };

  const handleRpmPackageDownload = async () => {
    setErrorMsg('');
    handleClose();
    setDownloadTriggerState(true);

    try {
      const data = await downloadRpmPackage().unwrap();
      downloadFile(data, 'Agent-Setup.rpm');
    } catch (error) {
      const msg = parseDownloadErrorMsg(error);
      setErrorMsg(msg);
    }

    setDownloadTriggerState(false);
  };

  const handleExePackageDownload = async () => {
    setErrorMsg('');
    setDownloadTriggerState(true);

    try {
      const data = await downloadExePackage().unwrap();
      downloadFile(data, 'Agent-Setup.exe');
    } catch (error) {
      const msg = parseDownloadErrorMsg(error);
      setErrorMsg(msg);
    }

    setDownloadTriggerState(false);
  };

  const getExeButtonText = () => {
    if (isExePackageLoading || isRpmPackageLoading || isDebPackageLoading) {
      return 'Downloading...';
    }

    if (errorMsg) {
      return 'Retry';
    }

    return 'Download';
  };

  return (
    <Card>
      <Stack direction="row" spacing={2}>
        <Icon
          id="lighthouse-logo"
          sx={{
            width: 80,
            height: 65,
          }}
        />
        <Box>
          <Typography variant="h2" sx={{ pb: 1 / 2 }}>
            SafeDNS agent for <br />{' '}
            <Typography
              component="span"
              variant="h2"
              textTransform="capitalize"
            >
              {osName}
            </Typography>
          </Typography>
          <Typography
            color="text.gray"
            variant="body1"
            sx={{ pb: 3, fontSize: '14px' }}
          >
            ({agentVersion})
          </Typography>
        </Box>
      </Stack>
      {Array.isArray(downloadMethods) ? (
        <>
          <Button
            variant="contained"
            fullWidth
            onClick={handleClick}
            endIcon={open ? <Icon id="expand" /> : <Icon id="expand" />}
            aria-controls={open ? 'download-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            disabled={isDownloadTriggered}
          >
            {getExeButtonText()}
            {isRpmPackageLoading || isDebPackageLoading ? (
              <CircularProgress size={15} sx={{ ml: 1, color: 'white' }} />
            ) : null}
          </Button>
          <Menu
            id="download-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                width: anchorEl?.clientWidth,
                mt: 0.5,
                boxShadow: '0 6px 12px 0 rgba(28, 39, 49, 0.05)',
                borderRadius: '8px',
                padding: '0',
              },
            }}
          >
            <MenuItem onClick={handleDebPackageDownload}>.deb</MenuItem>
            <MenuItem onClick={handleRpmPackageDownload}>.rpm</MenuItem>
          </Menu>
        </>
      ) : (
        <Button
          variant="contained"
          fullWidth
          disabled={isDownloadTriggered}
          onClick={handleExePackageDownload}
        >
          {getExeButtonText()}
          {isExePackageLoading ? (
            <CircularProgress size={15} sx={{ ml: 1, color: 'white' }} />
          ) : null}
        </Button>
      )}

      {errorMsg.length > 0 ? (
        <Typography
          variant="oldReleaseCardListItem"
          sx={{ pt: 1 / 2, mb: -3, color: 'error.main' }}
        >
          {errorMsg}
        </Typography>
      ) : null}

      <Typography
        variant="body1"
        sx={{
          pt: 3,
          color: '#414E62',
        }}
      >
        Release date:&nbsp;
        <Typography
          component="span"
          variant="body2"
          sx={{ fontSize: '14px', lineHeight: '20px', color: 'text.primary' }}
        >
          {releaseDate}
        </Typography>
      </Typography>
    </Card>
  );
};

export default ReleaseCard;
